import {
  collection,
  collectionGroup,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDoc,
  runTransaction,
  setDoc,
} from "firebase/firestore";
import { dbOrders } from "../database/firebaseOrders";

// Note: All order data is stored in the separate 'whubclasses' Firebase project.

let totalOrderReads = 0;

/**
 * Get the month key string for a given date (or today).
 * Format: "MMM-YYYY" e.g. "May-2026"
 * @param {Date|string} [date] - Optional date. Defaults to current date.
 * @returns {string} Month key
 */
export const getMonthKey = (date) => {
  const d = date ? new Date(date) : new Date();
  return d.toLocaleString("en-US", { month: "short" }) + "-" + d.getFullYear();
};

/**
 * Get the day key string for a given date (or today).
 * Format: "DD" e.g. "02"
 * @param {Date|string} [date] - Optional date. Defaults to current date.
 * @returns {string} Day key
 */
export const getDayKey = (date) => {
  const d = date ? new Date(date) : new Date();
  return String(d.getDate()).padStart(2, "0");
};

/**
 * Get next sequential bill number (atomic, starting from 0)
 * @returns {Promise<number>} Next bill number
 */
export const getNextBillNumber = async () => {
  const counterRef = doc(dbOrders, "Counters", "billNumber");
  const nextNumber = await runTransaction(dbOrders, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    if (!counterDoc.exists()) {
      await setDoc(counterRef, { current: 0 });
      return 0;
    }
    const current = counterDoc.data().current ?? -1;
    const next = current + 1;
    transaction.update(counterRef, { current: next });
    return next;
  });
  return nextNumber;
};

/**
 * Get next daily serial number (atomic, starts from 1 for each day)
 * @param {string} [monthKey] - Optional month key. Defaults to current month.
 * @param {string} [dayKey] - Optional day key. Defaults to current day.
 * @returns {Promise<number>} Next daily serial number
 */
export const getNextDailySerialNumber = async (
  monthKey = getMonthKey(),
  dayKey = getDayKey(),
) => {
  const counterRef = doc(
    dbOrders,
    "Orders",
    monthKey,
    "Days",
    dayKey,
    "Counters",
    "dailySerialNumber",
  );
  const nextNumber = await runTransaction(dbOrders, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    if (!counterDoc.exists()) {
      transaction.set(counterRef, { current: 1 });
      return 1;
    }
    const current = counterDoc.data().current ?? 0;
    const next = current + 1;
    transaction.update(counterRef, { current: next });
    return next;
  });
  return nextNumber;
};

/**
 * Fetch all orders from all months using collectionGroup.
 * Path: Orders/{monthKey}/orders
 * @returns {Promise<Array>} Array of order objects (each includes `monthKey`), sorted newest first
 */
export const getAllOrders = async () => {
  try {
    // No orderBy here — avoids needing a Firestore Collection Group index.
    // We sort in JS after fetching.
    const q = query(collectionGroup(dbOrders, "orders"));
    const querySnapshot = await getDocs(q);

    totalOrderReads += querySnapshot.docs.length;
    console.log(
      `[Firestore Read] Fetched ${querySnapshot.docs.length} orders. Total session reads: ${totalOrderReads}`,
    );

    const orders = [];
    querySnapshot.forEach((docSnap) => {
      // ref.parent is "orders"
      // ref.parent.parent is {dayKey}
      // ref.parent.parent.parent is "Days"
      // ref.parent.parent.parent.parent is {monthKey}
      const dayKey = docSnap.ref.parent.parent.id;
      const monthKey = docSnap.ref.parent.parent.parent.parent.id;
      orders.push({
        id: docSnap.id,
        monthKey,
        dayKey,
        ...docSnap.data(),
      });
    });

    // Sort newest first in JS
    orders.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.timestamp || 0);
      const dateB = new Date(b.createdAt || b.timestamp || 0);
      return dateB - dateA;
    });

    return orders;
  } catch (error) {
    console.error("Error fetching orders:", error);
    throw error;
  }
};

/**
 * Fetch all orders for a specific month by querying all 31 days.
 * This avoids needing a collectionGroup index and saves massive read costs
 * compared to fetching all time.
 * @param {string} monthKey - The month key to fetch (e.g. "May-2026")
 * @returns {Promise<Array>} Array of order objects, sorted newest first
 */
export const getOrdersForMonth = async (monthKey) => {
  try {
    const days = Array.from({ length: 31 }, (_, i) =>
      String(i + 1).padStart(2, "0"),
    );
    const promises = days.map((day) =>
      getDocs(collection(dbOrders, "Orders", monthKey, "Days", day, "orders")),
    );

    const snapshots = await Promise.all(promises);
    const orders = [];

    let monthReads = 0;
    snapshots.forEach((snap, i) => {
      monthReads += snap.docs.length;
      snap.forEach((docSnap) => {
        orders.push({
          id: docSnap.id,
          monthKey,
          dayKey: days[i],
          ...docSnap.data(),
        });
      });
    });

    totalOrderReads += monthReads;
    console.log(
      `[Firestore Read] Fetched ${monthReads} orders for ${monthKey}. Total session reads: ${totalOrderReads}`,
    );

    // Sort newest first
    orders.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.timestamp || 0);
      const dateB = new Date(b.createdAt || b.timestamp || 0);
      return dateB - dateA;
    });

    return orders;
  } catch (error) {
    console.error(`Error fetching orders for ${monthKey}:`, error);
    throw error;
  }
};

/**
 * Fetch orders with pagination using collectionGroup.
 * Fetches all docs and paginates in JS to avoid requiring a Collection Group index.
 * @param {number} page - Zero-based page number
 * @param {number} pageSize - Number of documents per page
 * @returns {Promise<Object>} { orders, hasMore }
 */
export const getOrdersPaginated = async (page = 0, pageSize = 25) => {
  try {
    // Fetch all, sort in JS, then slice — avoids Firestore index requirement
    const allOrders = await getAllOrders();
    const start = page * pageSize;
    const slice = allOrders.slice(start, start + pageSize);
    const hasMore = start + pageSize < allOrders.length;
    return { orders: slice, hasMore, total: allOrders.length };
  } catch (error) {
    console.error("Error fetching paginated orders:", error);
    throw error;
  }
};

/**
 * Fetch all orders for a specific user across all months.
 * Uses month-by-month scanning to avoid requiring a Firestore Collection Group index.
 * Scans the last 18 months to cover historical data.
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of user's orders (each includes `monthKey`), sorted newest first
 */
export const getUserOrders = async (userId) => {
  try {
    // Generate month keys for the last 18 months to cover historical orders
    const monthKeys = [];
    const now = new Date();
    for (let i = 0; i < 18; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key =
        d.toLocaleString("en-US", { month: "short" }) + "-" + d.getFullYear();
      monthKeys.push(key);
    }

    const days = Array.from({ length: 31 }, (_, i) =>
      String(i + 1).padStart(2, "0"),
    );

    // Fetch all days across all months in parallel
    const allPromises = monthKeys.flatMap((monthKey) =>
      days.map((day) =>
        getDocs(collection(dbOrders, "Orders", monthKey, "Days", day, "orders"))
          .then((snap) => ({ snap, monthKey, dayKey: day }))
          .catch(() => ({ snap: null, monthKey, dayKey: day })),
      ),
    );

    const results = await Promise.all(allPromises);

    const orders = [];
    let totalReads = 0;

    results.forEach(({ snap, monthKey, dayKey }) => {
      if (!snap) return;
      totalReads += snap.docs.length;
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        // Filter by userId in JavaScript — no index needed
        if (data.userId === userId) {
          orders.push({
            id: docSnap.id,
            monthKey,
            dayKey,
            ...data,
          });
        }
      });
    });

    totalOrderReads += totalReads;
    console.log(
      `[Firestore Read] Scanned ${totalReads} orders, found ${orders.length} for user ${userId}. Total session reads: ${totalOrderReads}`,
    );

    // Sort newest first
    orders.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.timestamp || 0);
      const dateB = new Date(b.createdAt || b.timestamp || 0);
      return dateB - dateA;
    });

    return orders;
  } catch (error) {
    console.error("Error fetching user orders:", error);
    throw error;
  }
};

/**
 * Add a new order under the current day's subcollection.
 * Path: Orders/{MMM-YYYY}/Days/{DD}/orders/{docId}
 * @param {Object} orderData - Order data
 * @returns {Promise<Object>} { success, orderId, orderNumber, monthKey, dayKey, message }
 */
export const addOrder = async (orderData) => {
  try {
    const monthKey = getMonthKey(); // e.g. "May-2026"
    const dayKey = getDayKey(); // e.g. "02"
    const ordersRef = collection(
      dbOrders,
      "Orders",
      monthKey,
      "Days",
      dayKey,
      "orders",
    );

    const orderEntry = {
      orderId: orderData.orderId,
      userId: orderData.userId,
      timestamp: orderData.timestamp,
      monthKey, // store for easy reference
      dayKey,
      ...(orderData.dailySerial !== undefined && {
        dailySerial: orderData.dailySerial,
      }),
      transaction: {
        type: orderData.transaction.type,
        paymentMethod: orderData.transaction.paymentMethod,
        status: orderData.transaction.status,
        ...(orderData.transaction.note && { note: orderData.transaction.note }),
      },
      items: orderData.items,
      summary: {
        grandTotalAmount: orderData.summary.grandTotalAmount,
        totalOrderVP: orderData.summary.totalOrderVP,
        shakeBalanceChange: orderData.summary.shakeBalanceChange,
        ...(orderData.summary.remainingShakesAfter !== undefined && {
          remainingShakesAfter: orderData.summary.remainingShakesAfter,
        }),
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Only include dailyHealthRecord if it exists
    if (orderData.dailyHealthRecord) {
      orderEntry.dailyHealthRecord = orderData.dailyHealthRecord;
    }

    const docRef = await addDoc(ordersRef, orderEntry);

    return {
      success: true,
      orderId: docRef.id,
      orderNumber: orderData.orderId,
      monthKey,
      dayKey,
      message: "Order created successfully",
    };
  } catch (error) {
    console.error("Error adding order:", error);
    throw error;
  }
};

/**
 * Update an order in its daily subcollection.
 * @param {string} orderId - Order document ID
 * @param {string} monthKey - Month key (e.g. "May-2026") stored on the order
 * @param {string} dayKey - Day key (e.g. "02") stored on the order
 * @param {Object} orderData - Partial order data to update
 * @returns {Promise<void>}
 */
export const updateOrder = async (orderId, monthKey, dayKey, orderData) => {
  try {
    const orderRef = doc(
      dbOrders,
      "Orders",
      monthKey,
      "Days",
      dayKey,
      "orders",
      orderId,
    );
    await updateDoc(orderRef, {
      ...orderData,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error updating order:", error);
    throw error;
  }
};

/**
 * Delete an order from its daily subcollection.
 * @param {string} orderId - Order document ID
 * @param {string} monthKey - Month key (e.g. "May-2026") stored on the order
 * @param {string} dayKey - Day key (e.g. "02") stored on the order
 * @returns {Promise<void>}
 */
export const deleteOrder = async (orderId, monthKey, dayKey) => {
  try {
    const orderRef = doc(
      dbOrders,
      "Orders",
      monthKey,
      "Days",
      dayKey,
      "orders",
      orderId,
    );
    await deleteDoc(orderRef);
  } catch (error) {
    console.error("Error deleting order:", error);
    throw error;
  }
};

/**
 * Update product stock count after order
 * @param {string} productId - Product document ID
 * @param {number} quantityToReduce - Quantity to reduce from stock
 * @returns {Promise<void>}
 */
export const updateProductStock = async (productId, quantityToReduce) => {
  try {
    const productRef = doc(dbOrders, "products", productId);
    const productSnap = await getDoc(productRef);

    if (productSnap.exists()) {
      const currentStock = productSnap.data().stockCount || 0;
      const newStock = Math.max(0, currentStock - quantityToReduce);

      await updateDoc(productRef, {
        stockCount: newStock,
        updatedAt: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error("Error updating product stock:", error);
    throw error;
  }
};
